// @ts-strict-ignore
import { useSnackBar } from '@usealloy/component-library';
import React, {
  useCallback, useContext, useMemo, useEffect,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import {
  selectActiveNodeEditorTab,
  selectDirectDependentActions,
  selectEditorContainerType,
  selectFocusedAction,
  selectIsEditingLinkedChildAction,
  selectNodeConnectorState,
  selectPreviewAction,
  selectWorkflowData,
  selectWorkflowOperationDescs,
  selectWorkflowProps,
  selectIsTagSwapMode,
  selectIsPreviewSnippetMode,
} from 'flow/store/redux/selectors';
import {
  dismissWorkflowOperationMessage,
  resetComponentState,
  resetNodeAndConnectorState,
  setActiveNodeEditorTab,
  updateAction,
  setIsTagSwapMode,
} from 'flow/store/redux/slice';
import { ActionType } from 'flow/store/engine/constants';
import type { NodeEditorTab } from 'flow/store/engine/types';
import { exitLinkedChildActionThunk } from 'flow/store/redux/thunks';

import { FormProvider } from 'react-hook-form';
import { NODE_EDITOR_DRAWER_HEIGHT_COLLAPSED } from 'flow/service/graph/layout/constants';
import {
  getActionTypeText,
  isActionOfType,
} from 'flow/service/actions';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import EditorErrorFallbackView from '../../EditorErrorFallbackView';

import type { NodeEditorDrawerTabConfig } from './types';
import useNodeEditorForm, { getFormAdapter } from './useNodeEditorForm';
import type { FormState } from './useNodeEditorForm';
import {
  getDrawerTitleIcon, getDrawerTitleText, getDrawerTabFooter, getDrawerTabs,
} from './helpers';

import { UserContext } from '~/dashboard/context';
import { findLastOperation } from 'flow/service/assistance';
import { useEditorAccess } from 'flow/editor/context';
import { StyledDrawer } from './styles';
import { getIsEditable } from 'flow/service/general';

interface NodeEditorDrawerProps {
  maxHeight?: number;
}

export default function NodeEditorDrawer({ maxHeight }: NodeEditorDrawerProps) {
  const dispatch = useAppDispatch();
  const { featureFlags, permissions, breadcrumbs } = useContext(UserContext);
  const focusedAction = useAppSelector(selectFocusedAction);
  const [isFormDisabled, setIsFormDisabled] = React.useState(false);
  const workflowData = useAppSelector(selectWorkflowData);
  const { services } = useAppSelector(selectWorkflowProps);
  const operationDescriptions = useAppSelector(selectWorkflowOperationDescs);
  const activeNodeEditorTab = useAppSelector(selectActiveNodeEditorTab);
  const focusedActionDependents = useAppSelector(selectDirectDependentActions);
  const previewActionBeingBuilt = useAppSelector(selectPreviewAction);
  const isEditingLinkedChildAction = useAppSelector(selectIsEditingLinkedChildAction);
  const connectorState = useAppSelector(selectNodeConnectorState);
  const editorContainerType = useAppSelector(selectEditorContainerType);
  const { editorMode } = useEditorAccess();
  const inEditMode = getIsEditable(editorMode);
  const isTagSwapMode = useAppSelector(selectIsTagSwapMode);
  const isPreviewSnippetMode = useAppSelector(selectIsPreviewSnippetMode);

  const addSnackbar = useSnackBar();
  const formMethods = useNodeEditorForm(
    focusedAction,
    activeNodeEditorTab,
    { disabled: isFormDisabled },
  );

  const isCreatingLinkedChildAction = connectorState.intent === 'add'
    && connectorState.interactionState === 'focused';

  const shouldShowEditorDrawer = isCreatingLinkedChildAction
    || (
      focusedAction
      && !isActionOfType(focusedAction, [ActionType.OUTCOME, ActionType.WORKFLOW])
    );

  const closeDrawer = useCallback(() => {
    const actionPublicId = focusedAction
      ? focusedAction.publicId
      : previewActionBeingBuilt?.publicId;
    if (isEditingLinkedChildAction && actionPublicId) {
      dispatch(exitLinkedChildActionThunk(previewActionBeingBuilt.publicId));
    }
    dispatch(resetNodeAndConnectorState());
    dispatch(resetComponentState());

    const lastAssistanceMessageToDismiss = findLastOperation({
      operations: operationDescriptions, publicId: actionPublicId,
    });
    if (lastAssistanceMessageToDismiss) {
      dispatch(dismissWorkflowOperationMessage({ id: lastAssistanceMessageToDismiss.id }));
    }
    if (isTagSwapMode.isActive) {
      dispatch(setIsTagSwapMode(false));
    }
  }, [dispatch, focusedAction, isEditingLinkedChildAction,
    operationDescriptions, previewActionBeingBuilt?.publicId, isTagSwapMode.isActive]);


  const handleTabChange = useCallback((value: NodeEditorTab) => {
    dispatch(setActiveNodeEditorTab(value));

    if (focusedAction?.publicId) {
      const lastAssistanceMessageToDismiss = findLastOperation({
        operations: operationDescriptions, publicId: focusedAction.publicId,
      });
      if (lastAssistanceMessageToDismiss) {
        dispatch(dismissWorkflowOperationMessage({ id: lastAssistanceMessageToDismiss.id }));
      }
    }
  }, [dispatch, focusedAction?.publicId, operationDescriptions]);

  // runs when the drawer is open and the user clicks another node
  useEffect(() => {
    if (shouldShowEditorDrawer && focusedAction) {
      dispatch(resetComponentState());

      // If we're switching to a different node while in tag swap mode, reset it
      if (isTagSwapMode.isActive) {
        dispatch(setIsTagSwapMode(false));
      }
    }
  }, [dispatch, focusedAction, shouldShowEditorDrawer, isTagSwapMode]);

  const handleFormAutoSave = React.useCallback((data: FormState) => {
    if (!inEditMode) return;
    const formAdapter = getFormAdapter(focusedAction, activeNodeEditorTab);
    dispatch(updateAction({
      path: `${focusedAction.publicId}`,
      value: formAdapter.mapFormDataToAction(data, focusedAction),
    }));
  }, [activeNodeEditorTab, dispatch, focusedAction, inEditMode]);

  const handleOnFormSubmit = React.useCallback(async (data: FormState) => {
    setIsFormDisabled(true);
    const formAdapter = getFormAdapter(focusedAction, activeNodeEditorTab);

    if (!featureFlags.useTyphonWorkflowEndpoints) {
      dispatch(updateAction({
        path: `${focusedAction.publicId}`,
        value: formAdapter.mapFormDataToAction(data, focusedAction),
      }));
      addSnackbar({
        title: `${getActionTypeText(focusedAction, true)} Updated`,
        type: 'success',
        duration: 2_000,
      });
    }

    dispatch(resetNodeAndConnectorState());

    setIsFormDisabled(false);
  }, [
    addSnackbar,
    dispatch,
    focusedAction,
    activeNodeEditorTab,
    featureFlags.useTyphonWorkflowEndpoints,
  ]);

  const drawerTitleText = getDrawerTitleText(focusedAction);
  const drawerTitleIcon = getDrawerTitleIcon(focusedAction);

  const drawerTabs = useMemo<NodeEditorDrawerTabConfig[]>(() => getDrawerTabs(
    {
      dataRoutes: workflowData?.dataRoutes,
      featureFlags,
      permissions,
      focusedAction,
      handleFormAutoSave,
      options: {
        isCreatingLinkedChildAction,
        numDependents: focusedActionDependents.length,
      },
      services,
      inEditMode,
      breadcrumbs,
    },
  ), [
    focusedAction,
    isCreatingLinkedChildAction,
    focusedActionDependents,
    featureFlags,
    permissions,
    handleFormAutoSave,
    workflowData,
    services,
    inEditMode,
    breadcrumbs,
  ]);

  const DrawerFooter = useMemo(
    () => (!inEditMode ? null : getDrawerTabFooter(activeNodeEditorTab, focusedAction)),
    [activeNodeEditorTab, focusedAction, inEditMode],
  );

  return (
    <ErrorBoundary
      fallbackRender={({ resetErrorBoundary, error }) => (
        <EditorErrorFallbackView
          inDrawer
          error={error}
          workflowToken={workflowData?.token}
          versionId={workflowData?.publicVersion.toString()}
          style={{
            width: 'auto',
            padding: 'var(--cl-space-5)',
            margin: 'var(--cl-space-5)',
            background: 'var(--cl-background-default)',
            borderRadius: 'var(--cl-radius-4)',
            boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.12)',
          }}
          resetErrorBoundary={resetErrorBoundary}
        />
      )}
      onReset={closeDrawer}
    >
      <FormProvider {...formMethods}>
        <StyledDrawer
          containerType={editorContainerType}
          disableAnimation={isPreviewSnippetMode}
          data-dd-privacy="allow"
          icon={drawerTitleIcon}
          isFloating
          isResizable
          showExpandButton={!isPreviewSnippetMode}
          footer={DrawerFooter && (
            /**
             * not every instance of DrawerFooter takes in the same props. we might
             * unify them by sharing the same interface for props in BUILD-857. in the meantime,
             * the props encompass all props that _might_ be required by all Footers
             */
            <DrawerFooter
              onSave={handleOnFormSubmit}
              onCancel={closeDrawer}
              saveDisabled={!formMethods?.formState.isDirty}
              setIsFormDisabled={setIsFormDisabled}
            />
          )}
          onTabChange={handleTabChange}
          placement="bottom"
          selectedTab={activeNodeEditorTab}
          show={shouldShowEditorDrawer}
          defaultSize={NODE_EDITOR_DRAWER_HEIGHT_COLLAPSED}
          maxSize={maxHeight}
          title={drawerTitleText}
          tabs={drawerTabs}
          toggleDrawer={closeDrawer}
          stickyFooter
          stickyHeader
        />
      </FormProvider>
    </ErrorBoundary>
  );
}
